import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Plane, Bus, Receipt, Sparkles, Plus, Trash2, GripVertical, ChevronUp, ChevronDown } from 'lucide-react';
import { Input } from '@/components/ui/Input/Input';
import { Button } from '@/components/ui/Button/Button';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { VenueOtherCostsFormData, VenueActivitiesBreakdown, MiscellaneousCostItem } from './types';
import { CURRENCY_INFO, type SupportedCurrency } from './types';

export interface OtherCostsProps {
  shortlistId: string;
  otherCosts: VenueOtherCostsFormData;
  activitiesBreakdown: VenueActivitiesBreakdown;
  attendees: number;
  displayCurrency: string;
  onOtherCostsChange: (data: Partial<VenueOtherCostsFormData>) => void;
  onActivitiesChange: (data: Partial<VenueActivitiesBreakdown>) => void;
}

export function OtherCosts({
  shortlistId,
  otherCosts,
  activitiesBreakdown,
  attendees,
  displayCurrency,
  onOtherCostsChange,
  onActivitiesChange,
}: OtherCostsProps) {
  const currency = displayCurrency as SupportedCurrency;
  const currencyInfo = CURRENCY_INFO[currency] ?? CURRENCY_INFO.USD;

  // Helper to format cents to dollars for display
  const centsToDisplay = (cents: number | null): string => {
    if (cents === null) return '';
    return (cents / 100).toFixed(2);
  };

  // Helper to convert dollars to cents
  const displayToCents = (display: string): number | null => {
    if (!display || display === '') return null;
    const num = parseFloat(display);
    if (isNaN(num)) return null;
    return Math.round(num * 100);
  };

  // Track which field is being edited
  const [editingField, setEditingField] = useState<string | null>(null);

  // Local state for inputs
  const [flightCost, setFlightCost] = useState(centsToDisplay(otherCosts.flight_cost_per_attendee));
  const [groundTransport, setGroundTransport] = useState(centsToDisplay(otherCosts.ground_transport_cost));

  // Sync with prop changes (only when not editing)
  useEffect(() => {
    if (editingField !== 'flight_cost') {
      setFlightCost(centsToDisplay(otherCosts.flight_cost_per_attendee));
    }
  }, [otherCosts.flight_cost_per_attendee, editingField]);

  useEffect(() => {
    if (editingField !== 'ground_transport') {
      setGroundTransport(centsToDisplay(otherCosts.ground_transport_cost));
    }
  }, [otherCosts.ground_transport_cost, editingField]);

  // Activities breakdown state
  const [activitiesNumber, setActivitiesNumber] = useState(activitiesBreakdown.activities_number?.toString() ?? '');
  const [activitiesCostPer, setActivitiesCostPer] = useState(centsToDisplay(activitiesBreakdown.activities_cost_per));

  useEffect(() => {
    if (editingField !== 'activities_number') {
      setActivitiesNumber(activitiesBreakdown.activities_number?.toString() ?? '');
    }
  }, [activitiesBreakdown.activities_number, editingField]);

  useEffect(() => {
    if (editingField !== 'activities_cost_per') {
      setActivitiesCostPer(centsToDisplay(activitiesBreakdown.activities_cost_per));
    }
  }, [activitiesBreakdown.activities_cost_per, editingField]);

  // Miscellaneous costs state (array), sorted by sort_order
  const miscCosts = useMemo(() => {
    const items = otherCosts.miscellaneous_costs ?? [];
    return [...items].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
  }, [otherCosts.miscellaneous_costs]);

  // Calculate totals for display
  const flightTotal = otherCosts.flight_cost_per_attendee
    ? (otherCosts.flight_cost_per_attendee / 100) * attendees
    : 0;

  // Ground Transport: rate is per person, so total = rate × attendees
  const groundTransportTotal = otherCosts.ground_transport_cost
    ? (otherCosts.ground_transport_cost / 100) * attendees
    : 0;

  // Activities: rate is per person, so total = rate × count × attendees
  const activitiesTotal = (activitiesBreakdown.activities_number ?? 0) *
    ((activitiesBreakdown.activities_cost_per ?? 0) / 100) *
    attendees;

  const miscTotal = miscCosts.reduce((sum, item) => {
    const amount = item.amount / 100;
    return sum + (item.type === 'per_person' ? amount * attendees : amount);
  }, 0);

  // Format currency for display
  const formatCurrency = useCallback((amount: number | null) => {
    if (amount === null || amount === 0) return '—';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  }, [currency]);

  // Handle field changes
  const handleFlightChange = (value: string) => {
    setFlightCost(value);
    onOtherCostsChange({ flight_cost_per_attendee: displayToCents(value) });
  };

  const handleGroundTransportChange = (value: string) => {
    setGroundTransport(value);
    onOtherCostsChange({ ground_transport_cost: displayToCents(value) });
  };

  const handleActivitiesNumberChange = (value: string) => {
    setActivitiesNumber(value);
    const numValue = value ? parseInt(value) : null;
    onActivitiesChange({ activities_number: numValue });
  };

  const handleActivitiesCostChange = (value: string) => {
    setActivitiesCostPer(value);
    onActivitiesChange({ activities_cost_per: displayToCents(value) });
  };

  // Miscellaneous costs handlers
  const addMiscItem = () => {
    const newItem: MiscellaneousCostItem = {
      id: crypto.randomUUID(),
      title: '',
      amount: 0,
      type: 'per_trip',
      sort_order: miscCosts.length,
    };
    onOtherCostsChange({ miscellaneous_costs: [...miscCosts, newItem] });
  };

  const updateMiscItem = (id: string, updates: Partial<MiscellaneousCostItem>) => {
    const updated = miscCosts.map((item) =>
      item.id === id ? { ...item, ...updates } : item
    );
    onOtherCostsChange({ miscellaneous_costs: updated });
  };

  const deleteMiscItem = (id: string) => {
    const filtered = miscCosts.filter((item) => item.id !== id);
    onOtherCostsChange({ miscellaneous_costs: filtered.length > 0 ? filtered : null });
  };

  // Drag-and-drop reordering
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = miscCosts.findIndex((item) => item.id === active.id);
    const newIndex = miscCosts.findIndex((item) => item.id === over.id);
    const reordered = arrayMove(miscCosts, oldIndex, newIndex).map((item, i) => ({
      ...item,
      sort_order: i,
    }));
    onOtherCostsChange({ miscellaneous_costs: reordered });
  };

  const moveMiscItem = (index: number, direction: 'up' | 'down') => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= miscCosts.length) return;
    const reordered = arrayMove(miscCosts, index, newIndex).map((item, i) => ({
      ...item,
      sort_order: i,
    }));
    onOtherCostsChange({ miscellaneous_costs: reordered });
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
      <div className="bg-gray-50 px-6 py-4 border-b border-gray-200 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center">
            <Receipt className="w-4 h-4 text-amber-600" />
          </div>
          <h3 className="font-semibold text-gray-900">Other Costs</h3>
          <span className="text-sm text-gray-500">({currencyInfo.name})</span>
        </div>
      </div>

      <div className="divide-y divide-gray-100">
        {/* Header Row */}
        <div className="grid grid-cols-12 gap-4 px-6 py-3 bg-gray-50 text-sm font-medium text-gray-500">
          <div className="col-span-5">Category</div>
          <div className="col-span-2 text-right">Rate/Unit</div>
          <div className="col-span-2 text-right">Count</div>
          <div className="col-span-2 text-right">Subtotal</div>
          <div className="col-span-1"></div>
        </div>

        {/* Activities Row */}
        <div className="grid grid-cols-12 gap-4 px-6 py-4 hover:bg-gray-50">
          <div className="col-span-5 flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-purple-600" />
            <span className="text-sm font-medium text-gray-900">Activities</span>
            <span className="text-xs text-gray-500">(per person)</span>
          </div>
          <div className="col-span-2">
            <Input
              type="number"
              min="0"
              step="0.01"
              value={activitiesCostPer}
              onChange={(e) => handleActivitiesCostChange(e.target.value)}
              onFocus={() => setEditingField('activities_cost_per')}
              onBlur={() => setEditingField(null)}
              className="text-right"
              placeholder="0.00"
              leftIcon={<span className="text-xs text-gray-500">{currencyInfo.symbol}</span>}
            />
          </div>
          <div className="col-span-2">
            <Input
              type="number"
              min="0"
              value={activitiesNumber}
              onChange={(e) => handleActivitiesNumberChange(e.target.value)}
              onFocus={() => setEditingField('activities_number')}
              onBlur={() => setEditingField(null)}
              className="text-right"
              placeholder="0"
            />
          </div>
          <div className="col-span-2 text-right flex items-center justify-end">
            <span className="text-sm font-medium text-gray-900">{formatCurrency(activitiesTotal)}</span>
          </div>
          <div className="col-span-1"></div>
        </div>

        {/* Flight Cost Row */}
        <div className="grid grid-cols-12 gap-4 px-6 py-4 hover:bg-gray-50">
          <div className="col-span-5 flex items-center gap-2">
            <Plane className="w-4 h-4 text-blue-600" />
            <span className="text-sm font-medium text-gray-900">Flight Cost</span>
            <span className="text-xs text-gray-500">(per person)</span>
          </div>
          <div className="col-span-2">
            <Input
              type="number"
              min="0"
              step="0.01"
              value={flightCost}
              onChange={(e) => handleFlightChange(e.target.value)}
              onFocus={() => setEditingField('flight_cost')}
              onBlur={() => setEditingField(null)}
              className="text-right"
              placeholder="0.00"
              leftIcon={<span className="text-xs text-gray-500">{currencyInfo.symbol}</span>}
            />
          </div>
          <div className="col-span-2 text-right flex items-center justify-end">
            <span className="text-sm text-gray-500">× {attendees}</span>
          </div>
          <div className="col-span-2 text-right flex items-center justify-end">
            <span className="text-sm font-medium text-gray-900">{formatCurrency(flightTotal)}</span>
          </div>
          <div className="col-span-1"></div>
        </div>

        {/* Ground Transport Row */}
        <div className="grid grid-cols-12 gap-4 px-6 py-4 hover:bg-gray-50">
          <div className="col-span-5 flex items-center gap-2">
            <Bus className="w-4 h-4 text-green-600" />
            <span className="text-sm font-medium text-gray-900">Ground Transport</span>
            <span className="text-xs text-gray-500">(per person)</span>
          </div>
          <div className="col-span-2">
            <Input
              type="number"
              min="0"
              step="0.01"
              value={groundTransport}
              onChange={(e) => handleGroundTransportChange(e.target.value)}
              onFocus={() => setEditingField('ground_transport')}
              onBlur={() => setEditingField(null)}
              className="text-right"
              placeholder="0.00"
              leftIcon={<span className="text-xs text-gray-500">{currencyInfo.symbol}</span>}
            />
          </div>
          <div className="col-span-2 text-right flex items-center justify-end">
            <span className="text-sm text-gray-500">× {attendees}</span>
          </div>
          <div className="col-span-2 text-right flex items-center justify-end">
            <span className="text-sm font-medium text-gray-900">{formatCurrency(groundTransportTotal)}</span>
          </div>
          <div className="col-span-1"></div>
        </div>

        {/* Miscellaneous Category Label (static, not draggable) */}
        {miscCosts.length > 0 && (
          <div className="grid grid-cols-12 gap-4 px-6 py-3 border-t border-gray-100">
            <div className="col-span-12 flex items-center gap-2">
              <Receipt className="w-4 h-4 text-orange-600 shrink-0" />
              <span className="text-sm font-medium text-gray-900">Miscellaneous</span>
            </div>
          </div>
        )}

        {/* Miscellaneous Costs Rows (sortable) */}
        {miscCosts.length > 0 && (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={miscCosts.map((item) => item.id)} strategy={verticalListSortingStrategy}>
              {miscCosts.map((item, index) => (
                <SortableMiscItemRow
                  key={item.id}
                  item={item}
                  currencySymbol={currencyInfo.symbol}
                  attendees={attendees}
                  onUpdate={(updates) => updateMiscItem(item.id, updates)}
                  onDelete={() => deleteMiscItem(item.id)}
                  onMoveUp={index > 0 ? () => moveMiscItem(index, 'up') : undefined}
                  onMoveDown={index < miscCosts.length - 1 ? () => moveMiscItem(index, 'down') : undefined}
                  isCompact={true}
                  showCategory={false}
                  formatCurrency={formatCurrency}
                />
              ))}
            </SortableContext>
          </DndContext>
        )}

        {/* Add Miscellaneous Item Row */}
        <div className="grid grid-cols-12 gap-4 px-6 py-3 bg-gray-50">
          <div className="col-span-5 flex items-center gap-2">
            {miscCosts.length === 0 && <Receipt className="w-4 h-4 text-orange-600" />}
            {miscCosts.length === 0 && <span className="text-sm font-medium text-gray-900">Miscellaneous</span>}
          </div>
          <div className="col-span-7 flex items-center justify-end">
            <Button variant="ghost" size="sm" onClick={addMiscItem} className="text-sm">
              <Plus className="w-4 h-4 mr-1" />
              Add Miscellaneous Item
            </Button>
            {miscCosts.length > 0 && (
              <span className="text-sm text-gray-500 ml-4">
                Total: {formatCurrency(miscTotal)}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// Sortable wrapper for MiscItemRow
function SortableMiscItemRow(props: MiscItemRowProps & { onMoveUp?: () => void; onMoveDown?: () => void }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: props.item.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 10 : undefined,
    opacity: isDragging ? 0.5 : undefined,
    position: 'relative' as const,
  };

  return (
    <div ref={setNodeRef} style={style}>
      <MiscItemRow
        {...props}
        dragHandleProps={{ ...attributes, ...listeners }}
      />
    </div>
  );
}

// Miscellaneous item row component
interface MiscItemRowProps {
  item: MiscellaneousCostItem;
  currencySymbol: string;
  attendees: number;
  onUpdate: (updates: Partial<MiscellaneousCostItem>) => void;
  onDelete: () => void;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
  isCompact?: boolean;
  showCategory?: boolean;
  formatCurrency?: (amount: number | null) => string;
  dragHandleProps?: Record<string, unknown>;
}

function MiscItemRow({ item, currencySymbol, attendees, onUpdate, onDelete, onMoveUp, onMoveDown, isCompact = false, showCategory = false, formatCurrency, dragHandleProps }: MiscItemRowProps) {
  const [title, setTitle] = useState(item.title);
  const [amount, setAmount] = useState((item.amount / 100).toFixed(2));

  // Sync with prop changes
  useEffect(() => {
    setTitle(item.title);
    setAmount((item.amount / 100).toFixed(2));
  }, [item.title, item.amount]);

  const handleTitleBlur = () => {
    if (title !== item.title) {
      onUpdate({ title });
    }
  };

  const handleAmountBlur = () => {
    const cents = Math.round(parseFloat(amount || '0') * 100);
    if (cents !== item.amount) {
      onUpdate({ amount: cents });
    }
  };

  const calculatedTotal = item.type === 'per_person'
    ? (item.amount / 100) * attendees
    : item.amount / 100;

  if (isCompact) {
    // Compact table layout - fits into the 12-column grid
    return (
      <div className="grid grid-cols-12 gap-4 px-6 py-3 hover:bg-gray-50">
        <div className="col-span-5 flex items-center gap-1">
          {/* Drag handle */}
          <button
            {...dragHandleProps}
            className="p-0.5 text-gray-300 hover:text-gray-500 cursor-grab active:cursor-grabbing touch-none shrink-0"
            tabIndex={-1}
          >
            <GripVertical className="w-4 h-4" />
          </button>
          {showCategory && <Receipt className="w-4 h-4 text-orange-600 shrink-0" />}
          {showCategory && <span className="text-sm font-medium text-gray-900 mr-2 shrink-0">Miscellaneous</span>}
          <Input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onBlur={handleTitleBlur}
            placeholder="Item name"
            className="text-sm flex-1 min-w-0"
          />
        </div>
        <div className="col-span-2">
          <Input
            type="number"
            min="0"
            step="0.01"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            onBlur={handleAmountBlur}
            className="text-right text-sm"
            placeholder="0.00"
            leftIcon={<span className="text-xs text-gray-500">{currencySymbol}</span>}
          />
        </div>
        <div className="col-span-2">
          <select
            value={item.type}
            onChange={(e) => onUpdate({ type: e.target.value as 'per_trip' | 'per_person' })}
            className="flex h-10 w-full rounded-md border border-gray-200 bg-white px-2 py-2 text-sm text-gray-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          >
            <option value="per_trip">Per Trip</option>
            <option value="per_person">Per Person</option>
          </select>
        </div>
        <div className="col-span-2 text-right flex items-center justify-end">
          <span className="text-sm font-medium text-gray-900">
            {formatCurrency ? formatCurrency(calculatedTotal) : new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(calculatedTotal)}
          </span>
        </div>
        <div className="col-span-1 flex items-center justify-end gap-0.5">
          {/* Arrow reorder buttons */}
          <div className="flex flex-col">
            <button
              onClick={onMoveUp}
              disabled={!onMoveUp}
              className="p-0 text-gray-300 hover:text-gray-600 disabled:opacity-0 disabled:pointer-events-none transition-colors"
              title="Move up"
            >
              <ChevronUp className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={onMoveDown}
              disabled={!onMoveDown}
              className="p-0 text-gray-300 hover:text-gray-600 disabled:opacity-0 disabled:pointer-events-none transition-colors"
              title="Move down"
            >
              <ChevronDown className="w-3.5 h-3.5" />
            </button>
          </div>
          <button
            onClick={onDelete}
            className="p-1 text-gray-400 hover:text-red-500 transition-colors"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  }

  // Legacy card layout (for backward compatibility)
  return (
    <div className="flex items-center gap-2 bg-gray-50 rounded-lg p-2">
      <GripVertical className="w-4 h-4 text-gray-400 cursor-grab" />
      <Input
        type="text"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        onBlur={handleTitleBlur}
        placeholder="Item name"
        className="flex-1"
      />
      <Input
        type="number"
        min="0"
        step="0.01"
        value={amount}
        onChange={(e) => setAmount(e.target.value)}
        onBlur={handleAmountBlur}
        className="w-28 text-right"
        placeholder="0.00"
        leftIcon={<span className="text-sm text-gray-600">{currencySymbol}</span>}
      />
      <select
        value={item.type}
        onChange={(e) => onUpdate({ type: e.target.value as 'per_trip' | 'per_person' })}
        className="flex h-10 rounded-md border border-gray-200 bg-white px-2 py-2 text-sm text-gray-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
      >
        <option value="per_trip">Per Trip</option>
        <option value="per_person">Per Person</option>
      </select>
      <span className="text-sm text-gray-500 w-20 text-right">
        {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(calculatedTotal)}
      </span>
      <button
        onClick={onDelete}
        className="p-1 text-gray-400 hover:text-red-500 transition-colors"
      >
        <Trash2 className="w-4 h-4" />
      </button>
    </div>
  );
}
